/**
 * System API User Service
 *
 * Uses the integration infrastructure (IntegrationFactory / RestClient) to call
 * the System API endpoints that manage Firebase Auth users.
 *
 * The integration config is read from globalConfigService.systemApiUserIntegration.
 * The baseUrl in that config already ends with "/fb/v1/users/", so paths
 * built here are relative to that base (e.g. "<uid>").
 */
import { integrationFactory, IntegrationHttpError } from '@rammetec/data-access';
import { rtLogService } from '@rammetec/core';
import { globalConfigService } from './globalConfigService.js';
class SystemApiUserService {
    /**
     * Resolves the integration client from the cached GlobalConfig.
     * Creates a new client on every call (clients are cheap, configs may change on reload).
     */
    getClient() {
        const config = globalConfigService.getIntegrationConfig('systemApiUserIntegration');
        if (!config) {
            throw new Error('[SystemApiUserService] Integration config "systemApiUserIntegration" not found in GlobalConfig. ' +
                'Ensure globalConfigService.load() was called and the config exists in Firestore.');
        }
        return integrationFactory.createClient(config);
    }
    /**
     * Lists Firebase Auth users (paginated) via System API.
     *
     * @param maxResults - Maximum number of users to return (default: 100, max: 1000)
     * @param pageToken  - Token for fetching the next page
     * @returns Object with users array and optional pageToken for next page
     */
    async listUsers(maxResults = 100, pageToken) {
        try {
            const client = this.getClient();
            const params = { maxResults: String(maxResults) };
            if (pageToken)
                params.pageToken = pageToken;
            const response = await client.get('', { params });
            rtLogService.info('[SystemApiUserService] Users listed', { count: response.data.users?.length, hasNextPage: !!response.data.pageToken });
            return response.data;
        }
        catch (error) {
            rtLogService.error('[SystemApiUserService] Failed to list users', { maxResults, error });
            throw error;
        }
    }
    /**
     * Gets a single Firebase Auth user by UID via System API.
     *
     * @param uid - The Firebase Auth user UID
     * @returns The user record
     */
    async getUserByUid(uid) {
        try {
            const client = this.getClient();
            const response = await client.get(uid);
            rtLogService.info('[SystemApiUserService] User fetched', { uid });
            return response.data;
        }
        catch (error) {
            rtLogService.error('[SystemApiUserService] Failed to get user', { uid, error });
            throw error;
        }
    }
    /**
     * Resolves minimal display names for many uids in one request (`POST /users/names`).
     * Missing uids are omitted from the response (not an error).
     */
    async getUserNames(uids) {
        const unique = [...new Set(uids.filter(Boolean))];
        const result = new Map();
        if (unique.length === 0)
            return result;
        try {
            const client = this.getClient();
            const response = await client.post('names', { uids: unique });
            for (const entry of response.data.names ?? []) {
                if (entry.id && entry.name) {
                    result.set(entry.id, entry.name);
                }
            }
            rtLogService.info('[SystemApiUserService] User names fetched', { requested: unique.length, resolved: result.size });
            return result;
        }
        catch (error) {
            rtLogService.error('[SystemApiUserService] Failed to get user names', { count: unique.length, error });
            throw error;
        }
    }
    /**
     * Looks up a user by email via System API. Returns `null` when no user exists
     * with that email (HTTP 404); throws on other errors.
     */
    async getUserByEmail(email) {
        try {
            const client = this.getClient();
            const response = await client.get(`by-email/${encodeURIComponent(email)}`);
            return response.data;
        }
        catch (error) {
            if (error instanceof IntegrationHttpError && error.statusCode === 404)
                return null;
            rtLogService.error('[SystemApiUserService] Failed to get user by email', { email, error });
            throw error;
        }
    }
    /**
     * Creates a new user via System API (Auth credentials + Firestore UserData doc,
     * written server-side with Admin SDK so client rules don't block it).
     *
     * @param input - email + password (required) and optional domain fields
     * @returns The created, merged user record
     */
    async createUser(input) {
        try {
            const client = this.getClient();
            const response = await client.post('', input);
            rtLogService.info('[SystemApiUserService] User created', { id: response.data.id, email: input.email });
            return response.data;
        }
        catch (error) {
            rtLogService.error('[SystemApiUserService] Failed to create user', { email: input.email, error });
            throw error;
        }
    }
    /**
     * Updates an existing user via System API. Auth fields (email/name/password/
     * disabled) and domain UserData fields (name/gender/inactivationDate)
     * are persisted in a single call; `active` + audit dates are stamped server-side.
     *
     * @param id      - The user id (Firebase Auth UID)
     * @param updates - Fields to update
     * @returns The updated, merged user record
     */
    async updateUser(id, updates) {
        try {
            const client = this.getClient();
            const response = await client.put(id, updates);
            rtLogService.info('[SystemApiUserService] User updated', { id, updatedFields: Object.keys(updates) });
            return response.data;
        }
        catch (error) {
            rtLogService.error('[SystemApiUserService] Failed to update user', { id, error });
            throw error;
        }
    }
    /**
     * Generates a password reset link for a user via System API.
     * The link contains an oobCode that can be used with Firebase's confirmPasswordReset().
     *
     * @param uid         - The Firebase Auth user UID
     * @param continueUrl - The URL where the user will reset their password (e.g. https://app.example.com/reset-password)
     * @returns Object with the generated link
     */
    async generatePasswordResetLink(uid, continueUrl) {
        try {
            const client = this.getClient();
            const response = await client.post(`${uid}/password-reset-link`, { continueUrl });
            rtLogService.info('[SystemApiUserService] Password reset link generated', { uid });
            return response.data;
        }
        catch (error) {
            rtLogService.error('[SystemApiUserService] Failed to generate password reset link', { uid, error });
            throw error;
        }
    }
    /**
     * Deletes a Firebase Auth user via System API.
     * The API also removes the user from all organizations (org/orgId/users/uid and user/uid/orgs).
     *
     * @param uid - The Firebase Auth user UID
     */
    async deleteUser(uid) {
        try {
            const client = this.getClient();
            await client.delete(uid);
            rtLogService.info('[SystemApiUserService] User deleted', { uid });
        }
        catch (error) {
            rtLogService.error('[SystemApiUserService] Failed to delete user', { uid, error });
            throw error;
        }
    }
}
export const systemApiUserService = new SystemApiUserService();
//# sourceMappingURL=systemApiUserService.js.map